import HorizontalDividers from 'components/CustomDividers/HorizontalDividers';
import VerticalDividers from 'components/CustomDividers/VerticalDividers';
import { DialogContentStyled, Label, LeftContentWrapper, RightContentWrapper } from 'components/Dialog/DialogContent';
import { LeftContent } from 'components/Dialog/DialogLeftContent';
import { Bottom, BottomActions, ConnectButton, RightForm } from 'components/Dialog/DialogRightContent';
import { ErrorMessage, Input, InputGrid } from 'components/Form';
import WarningIcon from '@mui/icons-material/Warning';
import React, { useEffect, useState } from 'react';
import { FormControl, FormControlLabel, MenuItem, Radio, RadioGroup, Select, SelectChangeEvent, Switch, ToggleButton, Typography } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { getTokopediaIntegrations, integrateTokopediaOS, integrateTokopediaPM } from 'api/marketPlaces';
import { useAppStore } from 'stores/appStore';
import { DialogProps } from 'interfaces/Props';
import { showAlert } from 'components/Alert';
import { channels, FSID_LIST } from 'shared/constants';
import config from 'config';
import { AsyncButton } from 'components/ButtonNew/Button';
import ButtonAuthorization from 'pages/Onboarding/StepTwo/ButtonAuthorization';

interface OfficialStoreValues {
  store_name: string;
  store_id: string;
  app_id: string;
  client_id: string;
  client_secret: string;
}
interface PowerMerchantValues {
  store_name: string;
  store_id: string;
  integration_type: string;
}

interface Integration {
  integration_id: number;
  integration_name: string;
}

type TokopediaProps = DialogProps & {
  chData?: {
    extra_info: {
      shopName: string;
      shopId: string;
      fsId: number;
    }
  }
  isConnected?: boolean;
}

const TokopediaContent: React.FunctionComponent<TokopediaProps> = ({ chData, isConnected = false, handleClosePrimary, getListChannel }) => {
  const { setShowConnectedPopup, indexAccordion, disablePrevButton } = useAppStore();
  const [integrationType, setIntegrationType] = useState('1');
  const [integrationList, setIntegrationList] = useState([]);
  const [isTcb, setTcb] = useState(false);
  const [busy, setBusy] = useState<boolean>(false);

  const handleIntegration = (event: SelectChangeEvent) => {
    setIntegrationType(event.target.value);
  };

  const {
    register: registerOfficialStore,
    handleSubmit: handleSubmitOfficialStore,
    formState: { errors: errorsOfficialStore },
  } = useForm<OfficialStoreValues>();
  const {
    register: registerPowerMerchant,
    handleSubmit: handleSubmitPowerMerchant,
    formState: { errors: errorsPowerMerchant },
    setValue
  } = useForm<PowerMerchantValues>();

  const onSubmitOfficialStore: SubmitHandler<OfficialStoreValues> = async (integration) => {
    try {
      const integrationData = {
        shopName: integration.store_name,
        isCustom: 'Custom',
        fsId: integration.app_id,
        shopId: integration.store_id,
        secret: integration.client_secret,
        clientId: integration.client_id,
        is_tokocabang: false
      };

      const res = await integrateTokopediaOS(integrationData);
      if (res && res.data && res.data.status !== 'error') {
        if (!disablePrevButton) {
          getListChannel();
          handleClosePrimary();
          location.replace(config.baseUrl + `/?integrated=true&activeTab=marketplace&id=${indexAccordion}`);
        } else {
          location.replace(config.baseUrl);
        }
      } else {
        showAlert('error', `${res?.data?.message}`, 5000);
      }
    } catch (err) {
      const error = err as Error;
      console.error(error);
      showAlert('error', `${error}`, 5000);
    }
  };
  const onSubmitPowerMerchant: SubmitHandler<PowerMerchantValues> = async (integration) => {
    try {
      const integrationData = {
        shopName: integration.store_name,
        shopId: integration.store_id,
        isCustom: integrationType,
        is_tokocabang: isTcb
      };

      const res = await integrateTokopediaPM(integrationData);
      if (res && res.data && res.data.status !== 'error') {
        if (!disablePrevButton) {
          setShowConnectedPopup(true);
          getListChannel();
          handleClosePrimary();
        } else {
          location.replace(config.baseUrl);
        }
      } else {
        showAlert('error', `${res?.data?.message}`, 5000);
      }
    } catch (err) {
      const error = err as Error;
      console.error(error);
      showAlert('error', `${error}`, 5000);
    } finally {
      setBusy(false);
    }
  };

  const initializationIntegration = async () => {
    try {
      const { data } = await getTokopediaIntegrations();
      setIntegrationList(data);
    } catch (err) {
      const error = err as Error;
      console.error(error);
    }
  };

  useEffect(() => {
    if (isConnected && chData?.extra_info) {
      // Set the store name from the connected data
      setValue('store_name', chData.extra_info.shopName);
      // Set the store ID from the connected data
      setValue('store_id', chData.extra_info.shopId);
      // Set the integration type based on the channel ID
      const integrId = FSID_LIST.indexOf(chData.extra_info.fsId) + 1;
      setIntegrationType(integrId !== 0 ? integrId.toString() : '1');
    }
  }, [isConnected, chData, setValue]);

  useEffect(() => {
    initializationIntegration();
  }, []);

  return (
    <DialogContentStyled>
      <LeftContentWrapper>
        <LeftContent channelId={channels.TOKOPEDIA} />
      </LeftContentWrapper>
      <VerticalDividers />
      <RightContentWrapper>
        <div style={{ padding: '2rem 1.3rem' }}>
          <RightForm no_padding={true} id="tokopedia_form" onSubmit={handleSubmitPowerMerchant(onSubmitPowerMerchant)}>
            <InputGrid
              label={<Label className="requiredField">Nama Toko</Label>}
              input={
                <Input
                  isError={errorsPowerMerchant.store_name?.message ? true : false}
                  type={'text'}
                  placeholder={'Masukkan Nama Toko'}
                  disabled={isConnected}
                  {...registerPowerMerchant('store_name', {
                    required: { value: true, message: 'Nama Toko harus di isi' }
                  })}
                />
              }
              errorMessage={
                errorsPowerMerchant?.store_name && (
                  <ErrorMessage
                    icon={<WarningIcon fontSize="small" color="error" />}
                    message={errorsPowerMerchant.store_name.message}
                  />
                )
              }
            />
            <InputGrid
              label={<Label className="requiredField">ID Toko</Label>}
              input={
                <Input
                  isError={errorsPowerMerchant.store_id?.message ? true : false}
                  type={'text'}
                  placeholder={'Masukkan ID Toko'}
                  disabled={isConnected}
                  {...registerPowerMerchant('store_id', {
                    required: { value: true, message: 'ID Toko harus di isi' }
                  })}
                />
              }
              errorMessage={
                errorsPowerMerchant?.store_id && (
                  <ErrorMessage
                    icon={<WarningIcon fontSize="small" color="error" />}
                    message={errorsPowerMerchant.store_id.message}
                  />
                )
              }
            />
            <InputGrid
              label={<Label className="requiredField">Jenis Integrasi</Label>}
              input={
                <FormControl fullWidth size="small" sx={{ padding: '0' }}>
                  <Select
                    value={integrationType}
                    onChange={handleIntegration}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                    disabled={isConnected}
                    sx={{ 
                      padding: '0',
                      fontSize: '13px'
                    }}
                    placeholder={'Pilih Jenis Integrasi'}
                  >
                    {integrationList.map((integration: Integration, index) => (
                      <MenuItem key={index} sx={{ fontSize: '12px' }} value={integration.integration_id}>
                        {integration.integration_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {!isConnected && (
                    <div className="d-flex align-items-center mt-1">
                      <Switch className="switch-small m-1" onChange={() => setTcb((prev) => !prev)} checked={isTcb} />
                      <Typography sx={{ fontSize: '14px' }}>Aktifkan sebagai Toko Cabang</Typography>
                    </div>
                  )}
                </FormControl>
              }
            />
          </RightForm>
        </div>
        <Bottom>
          <HorizontalDividers />
          <BottomActions>
            <ButtonAuthorization {...{
              form: 'tokopedia_form',
              disable: isConnected,
            }} />
          </BottomActions>
        </Bottom>
      </RightContentWrapper>
    </DialogContentStyled>
  );
};

export default TokopediaContent;
